import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  message,
  Table,
  DatePicker,
  Button,
  Space,
  Statistic,
  Input,
  Select,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import moment from "moment";
import API from "../../api";
import urls from "../../api/urls";
import "./style.scss";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Function to get fresh initial state with non-mutated moment objects
const getInitialState = () => ({
  pagination: { current: 1, pageSize: 10, total: 0 },
  dateRange: [
    moment().subtract(1, "days").startOf("day"),
    moment().endOf("day"),
  ], // Default to last 10 days
  columnFilters: {},
  searchableFields: [],
});

// Function to initialize column filters based on data keys
const initializeColumnFilters = (dataSource, searchableFieldsArray = []) => {
  if (!dataSource || dataSource.length === 0) {
    // Return filters for searchable fields even if no data
    const filters = {};
    searchableFieldsArray.forEach((field) => {
      filters[field] = "";
    });
    return Object.keys(filters).length > 0 ? filters : { awb_number: "" };
  }

  const sampleRecord = dataSource[0];
  const keys = Object.keys(sampleRecord);
  const filters = {};

  // Initialize filters only for searchable fields if provided
  if (searchableFieldsArray.length > 0) {
    searchableFieldsArray.forEach((field) => {
      if (keys.includes(field)) {
        filters[field] = "";
      }
    });
  } else {
    // Fallback to all keys if no searchable fields specified
    keys.forEach((key) => {
      filters[key] = "";
    });
  }

  return filters;
};

// Use the imported API endpoints
const { ANOMALIES_REPORT } = urls;

function AnomaliesReport() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const initialState = getInitialState();
  const [pagination, setPagination] = useState(initialState.pagination);
  const [dateRange, setDateRange] = useState(initialState.dateRange);
  const [columnFilters, setColumnFilters] = useState(
    initialState.columnFilters
  );
  const [searchableFields] = useState(initialState.searchableFields);
  const [totalAnomalies, setTotalAnomalies] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [postDeliveredStatusChange, setPostDeliveredStatusChange] =
    useState(true);
  const [deliveredAfterRtoInitiated, setDeliveredAfterRtoInitiated] =
    useState(true);
  const [incompleteShippingInformation, setIncompleteShippingInformation] =
    useState(true);
  const apiCallInProgress = useRef(false);
  const searchTimeouts = useRef({});

  // Generate search props for columns
  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);

            // Clear existing timeout
            if (searchTimeouts.current[dataIndex]) {
              clearTimeout(searchTimeouts.current[dataIndex]);
            }

            // Set new timeout for debounced search
            searchTimeouts.current[dataIndex] = setTimeout(() => {
              setColumnFilters((prev) => ({
                ...prev,
                [dataIndex]: e.target.value,
              }));
            }, 500);
          }}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setColumnFilters((prev) => ({
                ...prev,
                [dataIndex]: "",
              }));
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  // Generate columns dynamically based on data
  const generateColumns = (dataSource) => {
    if (!dataSource || dataSource.length === 0) {
      return [];
    }

    const sampleRecord = dataSource[0];
    const keys = Object.keys(sampleRecord);

    const columns = keys.map((key) => {
      const column = {
        title: key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        dataIndex: key,
        key: key,
        width: 150,
        ellipsis: true,
        render: (text) => {
          // Format dates
          if (key.includes("date") || key.includes("timestamp")) {
            return text ? moment(text).format("DD/MM/YYYY HH:mm:ss") : "-";
          }
          // Format boolean values
          if (typeof text === "boolean") {
            return text ? "Yes" : "No";
          }
          return text || "-";
        },
      };

      // Add search functionality for text fields
      if (
        typeof sampleRecord[key] === "string" &&
        !key.includes("date") &&
        !key.includes("timestamp")
      ) {
        column.sorter = (a, b) => (a[key] || "").localeCompare(b[key] || "");
        column.sortDirections = ["descend", "ascend"];
        column.showSorterTooltip = false;
        column.filterDropdown = getColumnSearchProps(
          key,
          column.title
        ).filterDropdown;
        column.filterIcon = getColumnSearchProps(key, column.title).filterIcon;
      }

      return column;
    });

    return columns;
  };

  // Fetch anomalies data
  const fetchAnomaliesData = async (page = 1, pageSize = 10, filters = {}) => {
    try {
      const queryParams = {
        start_date:
          dateRange[0]?.format("YYYY-MM-DD") ||
          moment().subtract(10, "days").format("YYYY-MM-DD"),
        end_date:
          dateRange[1]?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD"),
        post_delivered_status_change: postDeliveredStatusChange,
        delivered_after_rto_initiated: deliveredAfterRtoInitiated,
        incomplete_shipping_information: incompleteShippingInformation,
        page: page,
        limit: pageSize,
        ...filters,
      };

      const queryString = new URLSearchParams(queryParams).toString();

      const response = await API.Api(
        ANOMALIES_REPORT + "?" + queryString
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (response?.response?.data?.status === "success") {
        const newData = response.response.data.data || [];
        const totalCount = response.response.data.total_anomalies || 0;
        const totalOrdersCount = response.response.data.total_orders || 0;

        // Always clear existing data first
        setData([]);

        // Set new data in a timeout to ensure state updates are processed correctly
        setTimeout(() => {
          setData(newData);
          setPagination({
            current: page,
            pageSize: pageSize,
            total: totalCount,
          });
          setTotalAnomalies(totalCount);
          setTotalOrders(totalOrdersCount);

          // Initialize column filters if not already done
          if (Object.keys(columnFilters).length === 0 && newData.length > 0) {
            const initialFilters = initializeColumnFilters(
              newData,
              searchableFields
            );
            setColumnFilters(initialFilters);
          }
        }, 0);
      } else {
        message.error("Failed to fetch anomalies data");
        setData([]);
        setPagination({ current: 1, pageSize: 10, total: 0 });
      }
    } catch (error) {
      console.error("Error fetching anomalies data:", error);
      message.error("Error fetching anomalies data");
      setData([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
      throw error; // Re-throw to be caught by useEffect
    }
  };

  // Handle table pagination change
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(
      dates || [
        moment().subtract(10, "days").startOf("day"),
        moment().endOf("day"),
      ]
    );
  };

  // Handle refresh data
  const handleRefreshData = () => {
    // Force re-fetch by updating a dependency
    setDateRange([...dateRange]);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setColumnFilters({});
    setDateRange([
      moment().subtract(10, "days").startOf("day"),
      moment().endOf("day"),
    ]);
    setPostDeliveredStatusChange(true);
    setDeliveredAfterRtoInitiated(true);
    setIncompleteShippingInformation(true);
    setPagination({ current: 1, pageSize: 10, total: 0 });

    // Clear search timeouts
    Object.values(searchTimeouts.current).forEach((timeout) => {
      if (timeout) clearTimeout(timeout);
    });
    searchTimeouts.current = {};
  };

  // Handle export data
  const handleExportData = async () => {
    try {
      setLoading(true);

      const queryParams = {
        start_date:
          dateRange[0]?.format("YYYY-MM-DD") ||
          moment().subtract(10, "days").format("YYYY-MM-DD"),
        end_date:
          dateRange[1]?.format("YYYY-MM-DD") || moment().format("YYYY-MM-DD"),
        post_delivered_status_change: postDeliveredStatusChange,
        delivered_after_rto_initiated: deliveredAfterRtoInitiated,
        incomplete_shipping_information: incompleteShippingInformation,
        page: 1,
        limit: 10000,
        format: "csv",
        ...columnFilters,
      };

      const queryString = new URLSearchParams(queryParams).toString();

      const result = await API.Api(
        ANOMALIES_REPORT + "?" + queryString
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (result?.response?.status === 200 && result?.response?.data) {
        // Convert data to CSV
        const csvData = result.response.data.data || [];
        if (csvData.length === 0) {
          message.warning("No data to export");
          return;
        }

        const headers = Object.keys(csvData[0]);
        const csvContent = [
          headers.join(","),
          ...csvData.map((row) =>
            headers.map((header) => `"${row[header] || ""}"`).join(",")
          ),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Anomalies_Report_${moment().format("YYYY-MM-DD")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        message.success("Export successful");
      } else {
        message.error("Failed to export data");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      message.error("Error exporting data");
    } finally {
      setLoading(false);
    }
  };

  // Single effect to handle all data loading scenarios
  useEffect(() => {
    if (apiCallInProgress.current) {
      return;
    }

    const filtersToUse = { ...columnFilters };
    Object.keys(filtersToUse).forEach((key) => {
      if (!filtersToUse[key]) {
        delete filtersToUse[key];
      }
    });

    apiCallInProgress.current = true;
    setLoading(true);

    fetchAnomaliesData(
      pagination.current,
      pagination.pageSize,
      filtersToUse
    ).finally(() => {
      apiCallInProgress.current = false;
      setLoading(false);
    });
  }, [
    dateRange,
    columnFilters,
    postDeliveredStatusChange,
    deliveredAfterRtoInitiated,
    pagination.current,
    pagination.pageSize,
  ]);

  const columns = generateColumns(data);

  return (
    <div className="anomalies-report-container">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Card
            title={
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>Anomalies Report</span>
              </div>
            }
            extra={
              <Space wrap>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={handleRefreshData}
                  loading={loading}
                  size="small"
                >
                  <span className="button-text">Refresh Data</span>
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportData}
                  loading={loading}
                  size="small"
                >
                  <span className="button-text">Export</span>
                </Button>
              </Space>
            }
          >
            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8} md={8} lg={8}>
                <Card size="small">
                  <Statistic
                    title="Total Orders"
                    value={totalOrders}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8} md={8} lg={8}>
                <Card size="small">
                  <Statistic
                    title="Total Anomalies"
                    value={totalAnomalies}
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8} md={8} lg={8}>
                <Card size="small">
                  <Statistic
                    title="Anomaly Rate"
                    value={
                      totalOrders > 0
                        ? ((totalAnomalies / totalOrders) * 100).toFixed(2)
                        : 0
                    }
                    suffix="%"
                    valueStyle={{ color: "#722ed1" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Filters Section */}
            <div className="search-and-filter-section">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={24} md={6} lg={6}>
                  <div className="date-range-picker">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: 500,
                      }}
                    >
                      Date Range:
                    </label>
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      allowClear={false}
                      disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                      }
                      placeholder={["Start Date", "End Date"]}
                    />
                  </div>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: 500,
                      }}
                    >
                      Post Delivered Status Change:
                    </label>
                    <Select
                      value={postDeliveredStatusChange}
                      onChange={setPostDeliveredStatusChange}
                      style={{ width: "100%" }}
                    >
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: 500,
                      }}
                    >
                      Delivered After RTO Initiated:
                    </label>
                    <Select
                      value={deliveredAfterRtoInitiated}
                      onChange={setDeliveredAfterRtoInitiated}
                      style={{ width: "100%" }}
                    >
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={4} lg={6}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontWeight: 500,
                      }}
                    >
                      Incomplete Shipping Information:
                    </label>
                    <Select
                      value={incompleteShippingInformation}
                      onChange={setIncompleteShippingInformation}
                      style={{ width: "100%" }}
                    >
                      <Option value={true}>Yes</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={24} md={10} lg={10}>
                  <div
                    className="action-buttons-container"
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      height: "32px",
                      marginTop: "30px",
                    }}
                  >
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleClearFilters}
                      disabled={loading}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Data Table */}
            <div style={{ marginTop: 16 }}>
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
                onChange={handleTableChange}
                rowKey={(record, index) =>
                  record.awb_number ||
                  record.cawb_number ||
                  record.id ||
                  `row-${index}`
                }
                scroll={{ x: "max-content" }}
                size="small"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AnomaliesReport;
